import { Search, X } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/Input'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { cn } from '@/utils/cn'

interface SearchInputProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  label?: string
  className?: string
}

/** Debounced search box that keeps typing snappy while limiting API calls. */
export function SearchInput({ value, onChange, placeholder, label, className }: SearchInputProps) {
  const { t } = useTranslation()
  const [text, setText] = useState(value)
  const [lastValue, setLastValue] = useState(value)
  const debounced = useDebouncedValue(text, 300)

  // Follow external resets (e.g. "Clear filters") without an effect.
  if (value !== lastValue) {
    setLastValue(value)
    setText(value)
  }
  useEffect(() => {
    if (debounced !== value) onChange(debounced)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to the debounced text
  }, [debounced])

  return (
    <div className={cn('w-full sm:max-w-xs', className)}>
      <label className="sr-only" htmlFor="table-search">
        {label ?? t('Search')}
      </label>
      <Input
        id="table-search"
        type="search"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={placeholder ?? t('Search…')}
        leading={<Search />}
        trailing={
          text ? (
            <button
              type="button"
              onClick={() => setText('')}
              aria-label={t('Clear search')}
              className="grid size-6 place-items-center rounded-xs text-subtle hover:text-ink"
            >
              <X className="size-3.5" />
            </button>
          ) : null
        }
        className="[&::-webkit-search-cancel-button]:hidden"
      />
    </div>
  )
}

export function FilterBar({
  children,
  actions,
  className,
}: {
  children: ReactNode
  actions?: ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex flex-col gap-3 border-b border-line p-4 sm:flex-row sm:flex-wrap sm:items-center',
        className,
      )}
    >
      <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">{children}</div>
      {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
    </div>
  )
}
